import { alertController, modalController } from '@ionic/core';
import { Component, Event, EventEmitter, Fragment, h, Host, Prop, State } from '@stencil/core';
import { CalendarDoc, HolidayOrException, WorkingType } from 'common';
import { ScheduleService } from 'src/global/service/ScheduleService';
import { Calendar } from './model/Calendar';
import { ScheduleData } from './model/ScheduleData';
import { DateSelector } from 'src/global/ui/date-selector';

const DEFAULT_WORK_HOURS = { from: '08:00', to: '17:00' };
const WORKING_DAY_TYPE = WorkingType.working;
const NON_WORKING_DAY_TYPE = WorkingType.nonWorking;

@Component({
  tag: 'schedule-calendar-editor',
  styleUrl: 'schedule-calendar-editor.css',
})
export class ScheduleCalendarEditor {
  static async present(projectId: string, project: ScheduleData, calendar: Calendar): Promise<CalendarDoc | null> {
    const modal = await modalController.create({
      component: 'schedule-calendar-editor',
      cssClass: 'modal-height-100',
      componentProps: {
        calendar,
        projectId,
        project,
      },
    });
    return new Promise(async resolve => {
      modal.addEventListener('save', (e: CustomEvent) => {
        resolve(e.detail);
        modal.dismiss();
      });
      modal.addEventListener('dismiss', () => {
        resolve(null);
        modal.dismiss();
      });
      await modal.present();
    });
  }

  @Prop() calendar: Calendar;
  @Prop() projectId: string;
  @Prop() project: ScheduleData;

  @Event() save: EventEmitter<CalendarDoc>;
  @Event() dismiss: EventEmitter<void>;

  @State() editedCalendar: CalendarDoc;
  @State() hasChanges = false;
  @State() isSaving = false;
  @State() newExceptionFrom: string = '';
  @State() newExceptionTo: string = '';
  @State() newExceptionType: WorkingType = WorkingType.nonWorking;
  @State() newExceptionHoliday: string = 'independence_day';
  @State() newExceptionCustomName: string = '';

  async componentWillLoad() {
    // Find the calendar from project
    // Deep clone for editing
    this.editedCalendar = JSON.parse(JSON.stringify(this.calendar.doc));
    if (!Array.isArray(this.editedCalendar.exceptions)) {
      this.editedCalendar.exceptions = [];
    }
  }

  async closeModal() {
    if (this.hasChanges) {
      const alert = await alertController.create({
        header: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to close?',
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Discard',
            handler: () => {
              this.dismiss.emit();
            },
          },
        ],
      });
      await alert.present();
    } else {
      this.dismiss.emit();
    }
  }

  async saveCalendar() {
    this.isSaving = true;
    try {
      const savedCalendar = await ScheduleService.saveCalendar(this.projectId, this.editedCalendar);
      console.log('Calendar saved successfully:', savedCalendar);
      // Closes the modal and resolves ScheduleCalendarEditor.present() with the saved doc.
      this.save.emit(savedCalendar);
    } catch (error) {
      console.error('Error saving calendar:', error);
      const message = error.message || 'Failed to save calendar changes';
      const alert = await alertController.create({
        header: 'Error',
        message: message,
        buttons: ['OK'],
      });
      await alert.present();
    } finally {
      this.isSaving = false;
    }
  }

  updateDayWorking(day: keyof CalendarDoc, working: boolean) {
    const dayData = this.editedCalendar[day] as any;
    if (!dayData) {
      (this.editedCalendar as any)[day] = {
        type: working ? WORKING_DAY_TYPE : NON_WORKING_DAY_TYPE,
        hours: working ? [{ ...DEFAULT_WORK_HOURS }] : []
      };
    } else {
      dayData.type = working ? WORKING_DAY_TYPE : NON_WORKING_DAY_TYPE;
      if (working && (!dayData.hours || dayData.hours.length === 0)) {
        dayData.hours = [{ ...DEFAULT_WORK_HOURS }];
      } else if (!working) {
        dayData.hours = [];
      }
    }
    this.hasChanges = true;
    this.editedCalendar = { ...this.editedCalendar };
  }

  updateNewException(field: 'from' | 'to', value: string) {
    if (field === 'from') {
      this.newExceptionFrom = value || '';
    } else {
      this.newExceptionTo = value || '';
    }
  }

  updateNewExceptionType(value: WorkingType) {
    if (value === WorkingType.working || value === WorkingType.nonWorking) {
      this.newExceptionType = value;
    }
  }

  updateNewExceptionHoliday(value: string) {
    this.newExceptionHoliday = value || '';
  }

  updateNewExceptionCustomName(value: string) {
    this.newExceptionCustomName = value || '';
  }

  private normalizeDateValue(date: string): string {
    if (!date) return '';
    return date.split('T')[0] || date;
  }

  private formatDateValue(date: string): string {
    const normalized = this.normalizeDateValue(date);
    if (!normalized) return 'Select date';
    return normalized;
  }

  async showExceptionDateSelector(field: 'from' | 'to') {
    const currentDate = field === 'from' ? this.newExceptionFrom : this.newExceptionTo;
    const date = await DateSelector.present('Select Date', currentDate);
    if (date) {
      const normalizedDate = this.normalizeDateValue(date);
      if (field === 'from') {
        this.newExceptionFrom = normalizedDate;
      } else {
        this.newExceptionTo = normalizedDate;
      }
    }
  }

  async addException() {
    const from = this.newExceptionFrom;
    const to = this.newExceptionTo;
    if (!from || !to) {
      const alert = await alertController.create({
        header: 'Missing Dates',
        message: 'Please select both From and To dates.',
        buttons: ['OK'],
      });
      await alert.present();
      return;
    }
    if (from > to) {
      const alert = await alertController.create({
        header: 'Invalid Date Range',
        message: 'The From date must be earlier than or equal to the To date.',
        buttons: ['OK'],
      });
      await alert.present();
      return;
    }
    if (!this.newExceptionCustomName) {
      const alert = await alertController.create({
        header: 'Missing Name',
        message: 'Please select a holiday name, or enter a name for "Other".',
        buttons: ['OK'],
      });
      await alert.present();
      return;
    }

    const exception: HolidayOrException = {
      from,
      to,
      type: this.newExceptionType,
      name: this.newExceptionCustomName,
    };
    this.editedCalendar.exceptions = [...((this.editedCalendar.exceptions || []) as HolidayOrException[]), exception];
    this.editedCalendar = { ...this.editedCalendar };
    this.hasChanges = true;
    this.newExceptionFrom = '';
    this.newExceptionTo = '';
    this.newExceptionType = WorkingType.nonWorking;
    this.newExceptionHoliday = 'independence_day';
    this.newExceptionCustomName = '';
  }

  removeException(exceptionToRemove: HolidayOrException) {
    if (!this.editedCalendar.exceptions || this.editedCalendar.exceptions.length === 0) {
      return;
    }
    const exceptions = (this.editedCalendar.exceptions || []) as HolidayOrException[];
    const index = exceptions.findIndex(
      exception =>
        exception.from === exceptionToRemove.from &&
        exception.to === exceptionToRemove.to &&
        exception.type === exceptionToRemove.type &&
        (exception.name || '') === (exceptionToRemove.name || ''),
    );
    if (index < 0) {
      return;
    }
    const nextExceptions = [...exceptions];
    nextExceptions.splice(index, 1);
    this.editedCalendar.exceptions = nextExceptions;
    this.editedCalendar = { ...this.editedCalendar };
    this.hasChanges = true;
  }

  getSortedExceptions(): HolidayOrException[] {
    return [...((this.editedCalendar.exceptions || []) as HolidayOrException[])].sort((a, b) => {
      const byFrom = a.from.localeCompare(b.from);
      if (byFrom !== 0) return byFrom;
      return a.to.localeCompare(b.to);
    });
  }

  addHourRange(day: keyof CalendarDoc) {
    const dayData = this.editedCalendar[day] as any;
    if (dayData) {
      dayData.hours.push({ ...DEFAULT_WORK_HOURS });
      this.hasChanges = true;
      this.editedCalendar = { ...this.editedCalendar };
    }
  }

  removeHourRange(day: keyof CalendarDoc, index: number) {
    const dayData = this.editedCalendar[day] as any;
    if (dayData && dayData.hours.length > index) {
      dayData.hours.splice(index, 1);
      this.hasChanges = true;
      this.editedCalendar = { ...this.editedCalendar };
    }
  }
  updateCalendarName(value: string) {
    this.editedCalendar.name = value;
    this.hasChanges = true;
    this.editedCalendar = { ...this.editedCalendar };
  }

  updateHourRange(day: keyof CalendarDoc, index: number, field: 'from' | 'to', value: string) {
    const dayData = this.editedCalendar[day] as any;
    if (dayData && dayData.hours[index]) {
      dayData.hours[index][field] = value;
      this.hasChanges = true;
      this.editedCalendar = { ...this.editedCalendar };
    }
  }

  renderDayEditor(dayName: string, dayKey: keyof CalendarDoc) {
    const dayData = (this.editedCalendar[dayKey] as any) || { type: NON_WORKING_DAY_TYPE, hours: [] };
    const isWorking = dayData.type !== NON_WORKING_DAY_TYPE && dayData.hours && dayData.hours.length > 0;

    return (
      <ion-row>
        <ion-col size="12">
            <ion-card-header class="ion-no-padding ion-margin-bottom">
              <ion-checkbox
                checked={isWorking}
                onIonChange={(e) => this.updateDayWorking(dayKey, e.detail.checked)}
              >
                <ion-text color="light">
                  <strong>{dayName}</strong>
                </ion-text>
              </ion-checkbox>
            </ion-card-header>
            {isWorking && (
              <ion-card-content class="cc-padding">
                {dayData.hours?.map((hours: { from: string; to: string }, index: number) => (
                  <ion-row key={index} class="ion-align-items-end cc-margin-bottom">
                    <ion-col size="5">
                      <ion-input
                        type="time"
                        value={hours.from}
                        onIonChange={(e) => this.updateHourRange(dayKey, index, 'from', e.detail.value)}
                        label="From"
                        labelPlacement="stacked"
                      />
                    </ion-col>
                    <ion-col size="5">
                      <ion-input
                        type="time"
                        value={hours.to}
                        onIonChange={(e) => this.updateHourRange(dayKey, index, 'to', e.detail.value)}
                        label="To"
                        labelPlacement="stacked"
                      />
                    </ion-col>
                    <ion-col size="2" class="ion-text-end">
                      <ion-button
                        fill="clear"
                        color="danger"
                        onClick={() => this.removeHourRange(dayKey, index)}
                        disabled={dayData.hours.length === 1}
                      >
                        <ion-icon slot="icon-only" name="trash-outline" />
                      </ion-button>
                    </ion-col>
                  </ion-row>
                ))}
                <ion-button
                  fill="clear"
                  size="small"
                  slot="end"
                  onClick={() => this.addHourRange(dayKey)}
                >
                  <ion-icon slot="icon-only" name="add" color="primary" />
                  Add Time Range
                </ion-button>
              </ion-card-content>
            )}
        </ion-col>
      </ion-row>
    );
  }

  render() {
    if (!this.editedCalendar) {
      return (
        <Host class="concourse-modal">
          <div class="modal-content cc-scrollbars ion-text-center ion-padding">
            <ion-spinner />
            <p>Loading calendar...</p>
          </div>
        </Host>
      );
    }

    return (
      <Host class="concourse-modal">
        <modal-header modalTitle={`Edit Calendar: ${this.editedCalendar.name}`} onDismiss={() => this.closeModal()} />
        <div class="modal-content cc-scrollbars ion-padding">
          <ion-grid>
            <ion-row>
              <ion-input type="text" value={this.editedCalendar.name} onIonChange={(e) => this.updateCalendarName(e.detail.value || '')} label="Calendar Name" labelPlacement="stacked"></ion-input>
            </ion-row>
            <ion-row>
              <ion-col size="12">
                <ion-text color="medium">
                  Configure working hours for each day of the week
                </ion-text>
              </ion-col>
            </ion-row>
            {this.renderDayEditor('Sunday', 'sunday')}
            {this.renderDayEditor('Monday', 'monday')}
            {this.renderDayEditor('Tuesday', 'tuesday')}
            {this.renderDayEditor('Wednesday', 'wednesday')}
            {this.renderDayEditor('Thursday', 'thursday')}
            {this.renderDayEditor('Friday', 'friday')}
            {this.renderDayEditor('Saturday', 'saturday')}

            <ion-row>
              <ion-col size="12">
                  <ion-card-header class="ion-no-padding ion-margin-bottom">
                    <ion-text color="light">
                      <h5><strong>Calendar Exceptions</strong></h5>
                    </ion-text>
                  </ion-card-header>
                  <ion-card-content class="cc-padding">
                    <ion-text color="medium">
                      <p>Add or remove exception ranges.</p>
                    </ion-text>

                    <ion-list class="ion-no-padding">
                      {this.getSortedExceptions().length === 0 ? (
                        <ion-item lines="none">
                          <ion-label>
                            <ion-text color="medium">No exceptions configured.</ion-text>
                          </ion-label>
                        </ion-item>
                      ) : (
                        this.getSortedExceptions().map(exception => (
                          <ion-row key={`${exception.from}-${exception.to}-${exception.type}-${exception.name || ''}`} class="ion-align-items-center cc-margin-bottom">
                            <ion-col>
                              <h2>{exception.name || 'Unnamed Holiday'}</h2>
                              <p>
                                From: {this.formatDateValue(exception.from)} · To: {this.formatDateValue(exception.to)}
                              </p>
                              <ion-text color="medium">
                                <p><small>{exception.type === WorkingType.nonWorking ? 'Non-working' : 'Working'}</small></p>
                              </ion-text>
                            </ion-col>
                            <ion-col size="auto" class="ion-text-end">
                              <ion-button fill="clear" color="danger" onClick={() => this.removeException(exception)}>
                                <ion-icon slot="icon-only" name="trash-outline" />
                              </ion-button>
                            </ion-col>
                          </ion-row>
                        ))
                      )}
                    </ion-list>

                    <ion-item class="ion-no-padding">
                      Add Exception
                    </ion-item>

                    <ion-grid class="ion-no-padding">
                      <ion-row class="ion-padding-top">
                        <ion-col class="ion-padding-end" size="6" sizeMd="6">
                          <ion-input
                            type="text"
                            value={this.newExceptionCustomName}
                            onIonChange={(e) => this.updateNewExceptionCustomName(e.detail.value || '')}
                            label="Holiday or Exception"
                            labelPlacement="stacked"
                            placeholder="Enter holiday/exception name"
                          />
                        </ion-col>
                        <ion-col class="ion-padding-start" size="6" sizeMd="6">
                          <ion-select
                            value={this.newExceptionType}
                            onIonChange={(e) => this.updateNewExceptionType(e.detail.value)}
                            label="Type"
                            labelPlacement="stacked"
                          >
                            <ion-select-option value={WorkingType.nonWorking}>Non-working</ion-select-option>
                            <ion-select-option value={WorkingType.working}>Working</ion-select-option>
                          </ion-select>
                        </ion-col>
                      </ion-row>
                      <ion-row class="ion-align-items-center">
                        <ion-col>
                          <ion-button size="small" fill="outline" onClick={() => this.showExceptionDateSelector('from')}>
                            <ion-label>From: {this.formatDateValue(this.newExceptionFrom)}</ion-label>
                            <ion-icon slot="end" name="calendar-number-outline" />
                          </ion-button>
                          <ion-button size="small" fill="outline" onClick={() => this.showExceptionDateSelector('to')}>
                            <ion-label>To: {this.formatDateValue(this.newExceptionTo)}</ion-label>
                            <ion-icon slot="end" name="calendar-number-outline" />
                          </ion-button>
                        </ion-col>
                        <ion-col size="auto" class="ion-text-end">
                          <ion-button fill="solid" color="primary" onClick={() => this.addException()}>
                            <ion-icon slot="icon-only" name="add" color="dark" />
                          </ion-button>
                        </ion-col>
                      </ion-row>
                    </ion-grid>
                  </ion-card-content>
              </ion-col>
            </ion-row>
          </ion-grid>
        </div>

        <ion-footer>
          <ion-toolbar>
            <ion-button
              slot="end"
              color="primary"
              fill="solid"
              shape="round"
              onClick={() => this.saveCalendar()}
              disabled={!this.hasChanges || this.isSaving}
            >
              {this.isSaving ? <ion-spinner name="crescent" size="small" slot="start" /> : null}
              {this.isSaving ? 'Saving...' : 'Save'}
            </ion-button>
          </ion-toolbar>
        </ion-footer>
      </Host>
    );
  }
}
